import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import PasswordInput from 'components/field/PasswordInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { updateConnectedIntegration } from 'pages/OtherIntegrations/ConnectedIntegrations/ConnectedIntegrationsSlice';

import { OTHER_INTEGRATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import * as S from './IntegrationDynamicForm.styled';

/**
 * @author Burak Ak
 */

const IntegrationDynamicForm = NiceModal.create(({ fieldNames, logo, content, title, integrationID, smsConfigID }) => {
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const modal = useModal();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [data, setData] = useState(null);

	const getLabel = useCallback(
		(key) => {
			const translated = t(`${title}.${key}`);
			return translated === `${title}.${key}` ? t(key) : translated;
		},
		[title]
	);

	const schema = useMemo(
		() =>
			yup.object().shape({
				configName: yup.string().nullable().required(),
				status: yup.boolean(),
				senderName: yup.string().nullable().required(),
				username: fieldNames?.includes('username')
					? yup.string().nullable().required()
					: yup.string().nullable(),
				password: fieldNames?.includes('password')
					? yup.string().nullable().required()
					: yup.string().nullable(),
				token: fieldNames?.includes('token') ? yup.string().nullable().required() : yup.string().nullable()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			configName: null,
			status: true,
			senderName: null,
			username: null,
			password: null,
			token: null
		}
	});

	const {
		handleSubmit,
		reset,
		formState: { isDirty }
	} = formMethods;

	useEffect(() => {
		if (smsConfigID) {
			getBrandSettings();
		}
	}, [smsConfigID]);

	const getBrandSettings = useCallback(() => {
		setIsLoading(true);
		axiosPrivate
			.post(OTHER_INTEGRATIONS.GET_SMS_PROVIDER, {
				ID: smsConfigID
			})
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					setData(data);
					reset({
						...data
					});
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	});

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(
				smsConfigID ? OTHER_INTEGRATIONS.UPDATE_SMS_PROVIDER : OTHER_INTEGRATIONS.ADD_SMS_PROVIDER,
				{
					ID: smsConfigID ? smsConfigID : null,
					...data,
					integrationID
				}
			);
			if (response.status === 200) {
				showSuccess();
				modal.remove();
				smsConfigID &&
					dispatch(
						updateConnectedIntegration({
							smsConfigID,
							status: data.status,
							configName: data.configName
						})
					);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);

			if (error.response?.data.code === 'error.C014') {
				showError({
					message: 'error.configNameAlreadyExists'
				});
			} else showError();
		}
		setIsLoading(false);
	};

	const defaultColSpan = {
		md: 6
	};

	return (
		<Modal fullwidth maxWidth="small" className="modal-integrationDynamicForm">
			<ModalTitle>
				<S.Header>
					<S.LeftContainer>
						<S.Logo width={'6rem'} height={'6rem'} url={logo} />
					</S.LeftContainer>
					<S.RightContainer>
						<Typography variant="p1" fontWeight="500">
							{title}
						</Typography>
						<Typography style={{ color: '#8B8B8B' }} noWrap>
							{content}
						</Typography>
					</S.RightContainer>
				</S.Header>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<FormItem colSpan={defaultColSpan} label="configName" required isLoading={isLoading}>
						<StringInput name="configName" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="status" isLoading={isLoading}>
						<SwitchInput name="status" className="mt-3" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="senderName" required isLoading={isLoading}>
						<StringInput name="senderName" />
					</FormItem>

					{(data?.username || fieldNames?.includes('username')) && (
						<FormItem colSpan={defaultColSpan} label={getLabel('userName')} required isLoading={isLoading}>
							<StringInput name="username" />
						</FormItem>
					)}

					{(data?.password || fieldNames?.includes('password')) && (
						<FormItem colSpan={defaultColSpan} label={getLabel('password')} required isLoading={isLoading}>
							<PasswordInput name="password" />
						</FormItem>
					)}

					{(data?.token || fieldNames?.includes('token')) && (
						<FormItem colSpan={defaultColSpan} label={getLabel('token')} required isLoading={isLoading}>
							<StringInput name="token" />
						</FormItem>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					variant="outline"
					onClick={() => {
						NiceModal.show(ConfirmationModal, {
							yesAction: () => {
								modal.remove();
							}
						});
					}}
				/>
				<Button
					label="connect"
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					variant="blue"
					loading={isLoading}
				/>
			</ModalActions>
		</Modal>
	);
});

export default IntegrationDynamicForm;
